import React, { useState, useCallback, useMemo } from 'react';
import { Route, Redirect, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { palette } from '@m12s/component-library';

import MainLayout from 'components/Layouts/MainLayout';
import { MachineHeader } from 'components/MachineHeader';
import { LanguageModal } from 'components/LanguageModal';
import { Loading } from 'components/Loading';
import { P } from 'components/Text';
import Routes from 'lib/utils/routes';
import { getMachine } from 'lib/selectors/getMachine';
import { getOpenTraining } from 'lib/selectors/getOpenTraining';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getWorkOrderOpActionLoading } from 'lib/selectors/getWorkOrderOpActionLoading';

import { DowntimePopup } from 'components/DowntimePopup';
import { WorkflowPopup } from 'components/WorkflowPopup';
import { MessagePopup } from 'components/MessagePopup';
import { TrainingDoc } from 'components/TrainingDoc';
import { getHomeTab, getIsHomeTabContextReady } from 'lib/selectors/getTabs';
import {
  actionCloseLanguageModal,
  actionPatchSelectWorkOrderOpOptions,
  actionOpenSelectWorkOrderOp,
  actionOpenLaborTicketModal,
  actionOpenSetupActionModal,
} from 'lib/actions';
import { getLanguageModalOpen } from 'lib/selectors/getLanguageModalOpen';
import { getIsJobLaborTrackingEnabled } from 'lib/selectors/getIsJobLaborTrackingEnabled';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { getSkipSetupParts } from 'lib/selectors/getSkipSetupParts';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getEnabledBarcodeFields } from 'lib/selectors/getBarcodeFieldConfig';
import { formatSearchString } from 'lib/utils/barcode';
import { throwSuccessToast } from 'lib/utils/toast';
import useBarcodeScanHandler from 'lib/hooks/useBarcodeScanHandler';
import { BARCODE_HANDLER_PRIORITIES } from 'lib/constants/barcodePriorities';
import { useJobDisplayName } from 'lib/hooks/useJobDisplayName';
import { ControlActionModal } from './ControlActionModal';
import { ERPJobControlBar } from './ERPJobControlBar';
import { ERPMultiJobControlBar } from './ERPMultiJobControlBar';
import { DefaultJobControlBar } from './DefaultJobControlBar';
import MachineMainWrapper from './MachineMainWrapper';

const ScreenOverlay = styled.div`
  z-index: 100;
  position: absolute;
  top: 0;
  left: 0;
  background-color: ${palette.Grey100};
  width: 100vw;
  height: 100vh;
`;

const Page = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const isJobLaborTrackingEnabled = useSelector(getIsJobLaborTrackingEnabled);
  const showApmMultiJobBar = useSelector(getIsApmERPJobFlow);

  const openTraining = useSelector(getOpenTraining);
  const machine = useSelector(getMachine);
  const homeTab = useSelector(getHomeTab);
  const isHomeTabContextReady = useSelector(getIsHomeTabContextReady);
  const enabledBarcodeFields = useSelector(getEnabledBarcodeFields);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const skipSetupParts = useSelector(getSkipSetupParts);
  const currentLaborTicket = useSelector(getCurrentLaborTicket);
  const isSetupTicket = currentLaborTicket?.type?.toLowerCase() === 'setup';
  const useSetupActionModal = skipSetupParts && isSetupTicket;
  const workOrderOpActionLoading = useSelector(getWorkOrderOpActionLoading);

  const [controlAction, setControlAction] = useState(null);

  const languageModalOpen = useSelector(getLanguageModalOpen);
  const { getOrderOpDisplayName } = useJobDisplayName();

  // Memoize the order op display name to avoid unnecessary recomputation when dependencies have not changed
  const orderOpDisplayName = useMemo(() => {
    return getOrderOpDisplayName(currentWorkOrderOp);
  }, [getOrderOpDisplayName, currentWorkOrderOp]);

  const handleBarcodeScan = useCallback(
    (barcode) => {
      if (!machine?.id) {
        return;
      }

      if (currentWorkOrderOp) {
        throwSuccessToast(`Barcode scanned: ${barcode}`);

        if (useSetupActionModal) {
          dispatch(actionOpenSetupActionModal('submit'));
          return;
        }
        dispatch(actionOpenLaborTicketModal('submit'));
        return;
      }

      throwSuccessToast(`Barcode scanned: ${barcode}`);

      dispatch(actionOpenSelectWorkOrderOp());

      // Use setTimeout to ensure the open action is processed before setting new values
      setTimeout(() => {
        const firstField = enabledBarcodeFields[0] || 'workOrderId';
        const formattedSearch = formatSearchString(
          { [firstField]: barcode },
          enabledBarcodeFields
        );

        dispatch(
          actionPatchSelectWorkOrderOpOptions({
            isSearchMode: true,
            search: formattedSearch,
            fromBarcodeScan: true,
          })
        );

        // Once on SelectJob page, SelectWorkOrderOpModal will handle subsequent scans
        history.push(Routes.machineIdSelectJobPath(machine.id));
      }, 0);
    },
    [
      history,
      machine,
      dispatch,
      enabledBarcodeFields,
      currentWorkOrderOp,
      useSetupActionModal,
    ]
  );

  useBarcodeScanHandler('machine-home', handleBarcodeScan, {
    priority: BARCODE_HANDLER_PRIORITIES.MACHINE_HOME,
  });

  if (openTraining) {
    return <TrainingDoc />;
  }

  return (
    <>
      <Route exact path={Routes.machineIdHomePath.pattern}>
        {isHomeTabContextReady && homeTab && (
          <Redirect to={Routes.machineIdHomeTabPath(machine.id, homeTab)} />
        )}
      </Route>
      <Route
        path={Routes.machineIdHomeTabPath.pattern}
        render={({
          match: {
            params: { tab },
          },
        }) => {
          return (
            <>
              <WorkflowPopup />
              <DowntimePopup />
              <MessagePopup />
              {controlAction && (
                <ControlActionModal
                  controlAction={controlAction}
                  setControlAction={setControlAction}
                />
              )}
              <LanguageModal
                isOpen={languageModalOpen}
                onClose={() => {
                  return dispatch(actionCloseLanguageModal());
                }}
              />
              {workOrderOpActionLoading && (
                <ScreenOverlay>
                  <Loading
                    text={
                      <>
                        {currentWorkOrderOp && (
                          <P
                            style={{
                              fontWeight: '600',
                              fontSize: '1.25rem',
                              marginTop: '1rem',
                            }}
                          >
                            {`Resuming "${currentWorkOrderOp.workOrderId} | ${orderOpDisplayName}"`}
                          </P>
                        )}
                        <P style={{ fontSize: '1rem', marginTop: '.5rem' }}>
                          Please wait while we resume the production order
                        </P>
                      </>
                    }
                  />
                </ScreenOverlay>
              )}
              <MainLayout.Container direction="column">
                <MachineHeader />
                <MainLayout.Container direction="column">
                  <MachineMainWrapper tab={tab} />
                </MainLayout.Container>

                {(() => {
                  if (showApmMultiJobBar) return <ERPMultiJobControlBar />;
                  if (isJobLaborTrackingEnabled) return <ERPJobControlBar />;
                  return (
                    <DefaultJobControlBar setControlAction={setControlAction} />
                  );
                })()}
              </MainLayout.Container>
            </>
          );
        }}
      />
    </>
  );
};

export default Page;
